import React, { useContext, useEffect, useState } from 'react';
import {
  AccountDetail,
  AccountParams,
  NotificationFor,
  AccountType,
  EnergyTrackerData,
  PaymentExtensionEligibility,
  PaymentExtensionResponse,
  ServicePointDetail,
  IqbdStatusInfo,
} from '../../../__generated__/pge-types';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import withWidth, { WithWidth } from '@material-ui/core/withWidth';
import ToggleableOptionsList, {
  Option,
  ToggleableOptionsListContent,
} from '../../account-summary/toggleable-options-list/ToggleableOptionsList';
import CurrentBill from '../../account-summary/current-bill';
import ReconnectCard from '../../account-summary/reconnect-card';
import EnergyImpactCarousel from '../../account-summary/energy-impact-carousel/EnergyImpactCarousel';
import Paper from '@material-ui/core/Paper';
import ManageAlerts from '../../account-summary/manage-alerts/ManageAlerts.section';
import Backdrop from '../../backdrop';
import Drawer from '../../expansion-panel';
import DisconnectNotice from '../../disconnect-notice/DisconnectNotice';
import {
  useTranslation,
  UseTranslationHook,
} from '../../../hooks/useTranslation';
import {
  getAccountDetails,
  getPgeEnergyTrackerData,
  getEnergyTrackerData,
  getAccountDetailsForOutages,
  getOnPremDetailsWithOutageCause,
} from './queries';
import ROUTES from '../../../routes';
import useAuthQuery from '../../../hooks/useAuthQuery';
import { dateSuffixMap } from '../../../util/format';
import ErrorOverlay from '../../error/error-overlay';
import { navigate, useLocation } from '@reach/router';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import NoServiceAgreement from '../no-service-agreement';
import BroadcastMessage from '../broadcast-message/BroadcastMessage';
import useTPA from '../../need-more-time-to-pay/tpa/useTPA';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { CurrentUseCard } from '../energy-usage/current-use-card';
import usePgeQuery from '../../../hooks/usePgeQuery';
import { useAccountCustomerDashboard } from '../../../hooks/useAccountCustomerDashboard';
import { SSMDashboardCard } from '../../ssm-dashboard-card';
import { getAccountDetailsForPaymentExtension } from '../../../queries/paymentExtension.query';
import { useApolloClient } from '@apollo/react-hooks';
import { AccountDashboardIntercept } from '../../account-dashboard-intercept';
import { DashboardStaticCard } from '../../dashboard-static-card';
import BillComparisonImg from '../../../static/bill-comparison.svg';
import MyEnergyUseImg from '../../../static/my-energy-use-static.svg';
import { OutageCard } from '../outage-card';
import { ProgramsPanel } from '../../programs-panel';
import { Box, CircularProgress } from '@material-ui/core';
import { NextBestOffer } from '../../next-best-offer';
import PendingApplicationBanner from '../../pge-plus-form/PendingApplicationBanner';
import AlertBanner from '../../AlertBanner';
import { PlannedOutageCard } from '../../outage_v2/components/planned-outage-card';
import { useGetAccountPlannedOutageEvents } from '../../outage_v2/hooks/useGetAccountPlannedOutageEvents';
import EnergyJourneyCard from '../energy-journey';
import MyEnergyInsights from '../my-energy-insights';
import BusinessProgramsCard from '../business-programs';
import { isWithinPast60Days } from '../../../util/date-utils';

type Props = {
  accountParams: AccountParams | undefined;
  encryptedAccountNumber: string | null | undefined;
  encryptedPersonId: string | undefined;
  iqbdStatus?: IqbdStatusInfo | null;
  setIsDisonnected: (value: boolean) => void;
  setIsEligibleForReconnect: (value: boolean) => void;
  setIsReconnectInProgress: (value: boolean) => void;
  setIsLastPaymentDateGreater: (value: boolean) => void;
} & WithWidth;

const getBillingAndPaymentOptions = (
  selectedAccount: AccountDetail,
  t: UseTranslationHook['t'],
  iqbdStatus?: IqbdStatusInfo | null,
): Option[] => {
  const paymentOptions: Option[] = [];
  if (selectedAccount) {
    const {
      isPaperlessBillEnrolled,
      preferredDueDate,
      equalpay,
      accountType,
    } = selectedAccount;
    const autoPay: Option = {
      name: t('AUTO_PAY'),
      active: Boolean(selectedAccount?.autoPay?.isEnrolled),
      link: ROUTES.AUTO_PAY,
      subText: t('AUTO_PAY_PROGRAM_SUBTEXT'),
    };
    const paperlessBilling: Option = {
      name: t('PAPERLESS_BILL'),
      active: isPaperlessBillEnrolled?.result ?? false,
      link: ROUTES.PAPERLESS_BILLING,
      subText: t('PAPERLESS_PROGRAM_SUBTEXT'),
    };
    const preferredDueDateActive = Boolean(
      preferredDueDate?.dueDate?.preferredDueDate,
    );
    const preferredDueDateOption: Option = {
      name: preferredDueDateActive
        ? `${t('PREFERRED_DUE_DATE')}${preferredDueDateActive ? ': ' : ''}`
        : t('SELECT_YOUR_DUE_DATE'),
      active: preferredDueDateActive,
      data: {
        value: preferredDueDateActive
          ? `${preferredDueDate?.dueDate?.preferredDueDate}${dateSuffixMap(
              t,
              preferredDueDate?.dueDate?.preferredDueDate,
            )}`
          : '',
      },
      link: ROUTES.PREFERRED_DUE_DAY,
      subText: t('DUEDATE_PROGRAM_SUBTEXT'),
    };
    const paymentType: Option = {
      name: t('EQUAL_PAY_TITLE'),
      active: equalpay?.paymentPlanType! === 'EqualPay',
      disabled: false,
      link: ROUTES.EQUAL_PAY,
      subText: t('EQUAL_PAY_PROGRAM_SUBTEXT'),
    };

    paymentOptions.push(
      autoPay,
      ...(accountType === AccountType.Res ? [paymentType] : []),
      paperlessBilling,
      preferredDueDateOption,
    );

    if (iqbdStatus?.isEnrolled && iqbdStatus?.isSameAccountEnrolled) {
      paymentOptions.push({
        name: t('INCOME_QUALIFIED_BILL_DISCOUNT'),
        active: true,
        link: ROUTES.IQBD_FORM,
        subText: iqbdStatus.discountTier
          ? t('IQBD_DISCOUNT_PCT_MSG', {
              discount: iqbdStatus.discountTier.replace('PCT', '%'),
            })
          : t('IQBD_DISCOUNT_MSG'),
      });
    }
  }

  return [...paymentOptions];
};

function SingleAccountSummary({
  accountParams,
  encryptedAccountNumber,
  encryptedPersonId,
  iqbdStatus,
  width,
  setIsDisonnected,
  setIsEligibleForReconnect,
  setIsReconnectInProgress,
  setIsLastPaymentDateGreater,
}: Props) {
  const { isResAccount } = useAccountCustomerDashboard();
  const { customer } = useAccountCustomer();
  const isMobile = ['xs', 'sm'].includes(width);
  const [showDisconnectNotice, toggleDisconnectNotice] = useState<boolean>(
    false,
  );
  const [
    outageDetails,
    setOutageDetails,
  ] = useState<ServicePointDetail | null>();
  const {
    isPGEPlusEVEnabled,
    isSecureSiteModalEnabled,
    isShowAccountSummaryMessage,
  } = useContext(featureFlagsContext);
  const location = useLocation();
  const { wrapWithLoader } = useWrapWithLoader();

  const { t } = useTranslation();
  const { tpaDetails } = useTPA({ shouldRedirect: false });
  const [paymentExtentionData, setPaymentExtentionData] = useState<
    PaymentExtensionResponse | undefined
  >();
  const { plannedOutageData } = useGetAccountPlannedOutageEvents(
    accountParams?.accountNumber,
  );
  const { loading: hookLoading, data } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetails, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    errorPolicy: 'all',
    skip: !accountParams,
  });

  const apolloClient = useApolloClient();
  const getOutageDetails = wrapWithLoader(async () => {
    if (!accountParams) {
      return;
    }
    const result = await apolloClient.query<{
      getAccountDetails: Array<AccountDetail>;
    }>({
      query: getAccountDetailsForOutages,
      variables: {
        params: {
          accountNumberList: [accountParams],
        },
      },
      fetchPolicy: 'network-only',
    });
    const outageReportedPremiseInfo = result?.data?.getAccountDetails[0].premiseInfo?.find(
      premiseInfo => {
        return premiseInfo?.servicePointDetails
          ? premiseInfo?.servicePointDetails?.filter(point => {
              return point?.outageInfo?.hasKnownOutage;
            }).length > 0
          : [];
      },
    );
    setOutageDetails(
      outageReportedPremiseInfo?.servicePointDetails?.find(
        outage => outage?.outageInfo?.hasKnownOutage,
        // TODO: Removed for now: && outage?.meter?.status === 'off',
      ),
    );
  });

  useEffect(() => {
    const fetchPaymentExtensionDetails = async () => {
      if (!accountParams) {
        return;
      }
      const result = await apolloClient.query<{
        getAccountDetails: Array<AccountDetail>;
      }>({
        query: getAccountDetailsForPaymentExtension,
        variables: {
          params: {
            accountNumberList: [accountParams],
          },
        },
        fetchPolicy: 'network-only',
      });
      const paymentExtensionInfo =
        result?.data?.getAccountDetails?.[0]?.paymentExtensionInfo;
      setPaymentExtentionData(paymentExtensionInfo!);
    };
    void getOutageDetails();
    isResAccount && void fetchPaymentExtensionDetails();
  }, [
    accountParams?.accountNumber,
    accountParams?.encryptedPersonId,
    isResAccount,
  ]);

  useEffect(() => {
    location.hash !== '#energyUse' && window.scrollTo(0, 0);
  }, []);

  const loading = hookLoading || !data || !accountParams;
  const account = data?.getAccountDetails?.[0];
  const isDisconnected = Boolean(account?.serviceConnectivity?.isDisconnected);
  const isEligibleForReconnect = Boolean(
    account?.serviceConnectivity?.isEligibleForReconnect,
  );
  const isReconnectInProgress = Boolean(
    account?.serviceConnectivity?.isReconnectInProgress,
  );
  const { data: onPremData } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getOnPremDetailsWithOutageCause, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
      commPreferencesParams: {
        notificationFor: NotificationFor.Alert,
      },
    },
    errorPolicy: 'all',
    skip: !accountParams,
  });

  const outageCause =
    outageDetails?.outageInfo?.cause ??
    onPremData?.getAccountDetails?.[0].premiseInfo?.[0]
      ?.servicePointDetails?.[0]?.outageInfo?.cause;
  const serviceStartDate =
    onPremData?.getAccountDetails?.[0]?.premiseInfo?.[0]?.saDetails?.[0]
      ?.startDate;

  const { loading: eTLoading, data: etData } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getPgeEnergyTrackerData, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    fetchPolicy: 'network-only',

    // eslint-disable-next-line @typescript-eslint/no-empty-function,no-empty-function
    onError: () => {},
    skip: !accountParams,
  });

  const isSchedule32CommercialAccount =
    etData?.getAccountDetails?.[0]?.pgeEnergyTracker?.energyTrackerInfo
      ?.serviceProvider === 'PGE-32';

  const {
    loading: pgeETWidgetLoading,
    data: pgeETWidgetData,
    error: pgeETWidgetError,
  } = usePgeQuery<{
    getEnergyTrackerData: EnergyTrackerData;
  }>(getEnergyTrackerData, {
    variables: {
      params: {
        encryptedAccountNumber,
        encryptedPersonId,
      },
    },
    skip: eTLoading || (!isResAccount && !isSchedule32CommercialAccount),
    errorPolicy: 'all',
  });

  const pgeEnergyTrackerData = etData?.getAccountDetails?.[0]?.pgeEnergyTracker;

  const energyTrackerDataProps = {
    pgeEnergyTrackerData: pgeEnergyTrackerData,
    encryptedAccountNumber: encryptedAccountNumber,
    encryptedPersonId: encryptedPersonId,
  };

  useEffect(() => {
    location.hash === '#energyUse' &&
      document.getElementById('energyUse')?.scrollIntoView();
  }, [etData?.getAccountDetails?.[0]?.pgeEnergyTracker]);

  useEffect(() => {
    if (
      account?.pendingDisconnect?.isPendingDisconnect &&
      showDisconnectNotice === false
    ) {
      // FIXME: Having this run immediately, without the setTimeout
      // causes an issue with the EnergyImpactCarousel, specifically
      // the Slider component it is built on.
      setTimeout(() => toggleDisconnectNotice(true));
    }
  }, [account?.pendingDisconnect?.isPendingDisconnect]);

  useEffect(() => {
    setTimeout(() =>
      setIsDisonnected(account?.serviceConnectivity?.isDisconnected!),
    );

    setTimeout(() =>
      setIsEligibleForReconnect(
        account?.serviceConnectivity?.isEligibleForReconnect!,
      ),
    );
    setTimeout(() =>
      setIsReconnectInProgress(
        account?.serviceConnectivity?.isReconnectInProgress!,
      ),
    );
    setTimeout(() =>
      setIsLastPaymentDateGreater(
        account?.serviceConnectivity?.disconnectionDate &&
          account?.billInfo?.lastPaymentDate &&
          account?.billInfo?.lastPaymentDate >
            account?.serviceConnectivity?.disconnectionDate
          ? true
          : false,
      ),
    );
  }, [account?.serviceConnectivity, account?.accountNumber]);

  useEffect(() => {
    // If we aren't loading, but we dont have an account, there was
    // an error pulling the requested account, so lets forward to the Error
    // page
    if (!loading && !account && accountParams) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(ROUTES.ERROR);
    }
  }, [data, account, loading, accountParams]);

  if (customer?.accountMeta?.totalAccounts === 0) {
    return (
      <Grid item xs={12}>
        <NoServiceAgreement />
      </Grid>
    );
  }

  if (!account || loading) {
    return <Backdrop forceOpen message={t('STILL_LOADING')} />;
  }

  if ((customer?.accountMeta?.totalAccounts ?? 0) > 0) {
    return (
      <>
        <BroadcastMessage />

        {!isDisconnected &&
          showDisconnectNotice &&
          account?.pendingDisconnect?.isPendingDisconnect &&
          account?.billInfo?.amountDue! > 0 && (
            <Grid item>
              <DisconnectNotice
                dismissDisconnectNotice={() => toggleDisconnectNotice(false)}
              />
            </Grid>
          )}
        {plannedOutageData &&
          plannedOutageData?.accountPlannedOutageEvent &&
          plannedOutageData?.accountPlannedOutageEvent.length > 0 && (
            <Grid item>
              <PlannedOutageCard
                plannedOutageEvents={
                  plannedOutageData?.accountPlannedOutageEvent
                }
              />
            </Grid>
          )}

        {outageDetails && (
          <Grid item>
            <OutageCard
              outageDetails={outageDetails}
              accountParams={accountParams}
              customerContact={
                customer && customer?.contactDetails && customer?.contactDetails
              }
            />
          </Grid>
        )}

        {!isReconnectInProgress && isDisconnected ? (
          <>
            <Grid item>
              <Grid container direction="row" spacing={2} alignItems="stretch">
                <Grid item xs={12} md={7}>
                  <Card>
                    <CardContent>
                      <ReconnectCard
                        isEligibleForReconnect={isEligibleForReconnect}
                        billInfo={account?.billInfo!}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Hidden smDown>
                  <Grid item sm={12} md={5}>
                    <Card>
                      <CardContent>
                        {account?.paymentEligibility ||
                        account?.isPaperlessBillEnrolled ||
                        account?.preferredDueDate?.dueDate ||
                        account?.equalpay ? (
                          <ToggleableOptionsList
                            listTitle={t('BILLING_AND_PAYMENT_SETTINGS')}
                            options={getBillingAndPaymentOptions(
                              account,
                              t,
                              iqbdStatus,
                            )}
                            renderInCard={false}
                            isReconnect={isDisconnected}
                          />
                        ) : (
                          <ErrorOverlay
                            title={t('BILLING_AND_PAYMENT_SETTINGS')}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Hidden>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item>
              {isShowAccountSummaryMessage && (
                <AlertBanner
                  hide={account.accountType !== AccountType.Res}
                  title={t('2025_PRICE_INCREASE_TITLE')}
                  text={t('2025_PRICE_INCREASE_TEXT')}
                  learnMoreLink={t('2025_PRICE_INCREASE_LEARN_MORE_LINK')}
                />
              )}
              {isPGEPlusEVEnabled && <PendingApplicationBanner />}

              <CurrentBill
                totalPendingCharges={
                  account?.pendingCharges?.totalPendingCharges
                }
                billInfo={account?.billInfo || null}
                autoPay={account?.autoPay || null}
                isNewAccount={account?.billInfo?.isNewAccount}
                isPendingDisconnect={
                  account.pendingDisconnect?.isPendingDisconnect
                }
                billDetails={account?.billInfo?.billDetails || null}
                isSummaryBillAccount={account?.isSummaryBillAccount!}
                optionList={getBillingAndPaymentOptions(account, t, iqbdStatus)}
                isTpaEligible={
                  tpaDetails
                    ? tpaDetails?.isEnrolled ||
                      tpaDetails?.isEligible ||
                      (paymentExtentionData?.eligibility &&
                        paymentExtentionData?.eligibility !==
                          PaymentExtensionEligibility.Ineligible) ||
                      paymentExtentionData?.isEnrolled!
                    : false
                }
                isResAccount={isResAccount}
              />
              {!Boolean(account?.billInfo?.billDetails?.amountDue) &&
                isResAccount && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <ManageAlerts />
                  </div>
                )}
            </Grid>
          </>
        )}

        {isResAccount && (
          <>
            <Grid item>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {eTLoading || pgeETWidgetLoading ? (
                    <Paper>
                      <CardContent
                        css={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <CircularProgress />
                      </CardContent>
                    </Paper>
                  ) : Boolean(pgeETWidgetError) ||
                    (etData?.getAccountDetails?.[0]?.pgeEnergyTracker
                      ?.energyTrackerInfo?.showEnergyTracker &&
                      pgeETWidgetData?.getEnergyTrackerData
                        ?.detailsAvailable) ? (
                    <CurrentUseCard
                      loading={pgeETWidgetLoading}
                      data={pgeETWidgetData}
                      error={Boolean(pgeETWidgetError)}
                      accountParams={accountParams}
                      isResAccount={isResAccount}
                    />
                  ) : (
                    <DashboardStaticCard
                      title={t('BILL_COMPARISON_TITLE')}
                      subText={t('BILL_COMPARISON_SUBTEXT')}
                      footerText={t('VIEW_BILL_DETAILS')}
                      image={<BillComparisonImg />}
                      path={`${ROUTES.VIEW_BILL}#energyUse`}
                    />
                  )}
                  <Box style={{ marginTop: '1rem' }}>
                    <DashboardStaticCard
                      title={t('MY_ENERGY_USE')}
                      subText={t('FIND_OUT_WHAT_IS_COSTING_YOU_2')}
                      footerText={t('ENERGY_USE_DETAILS')}
                      image={<MyEnergyUseImg />}
                      path={ROUTES.MY_ENERGY_USE}
                    />
                  </Box>
                </Grid>
                {!isMobile && (
                  <Grid item xs={12} sm={6}>
                    <ProgramsPanel />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {isMobile && (
              <Grid item>
                <ProgramsPanel />
              </Grid>
            )}

            <Grid item>
              <NextBestOffer accountType="residential" />
            </Grid>

            <Grid item>
              <SSMDashboardCard />
            </Grid>
          </>
        )}

        {/* Show all the component that are for commercial dashboard */}
        {!isResAccount && (
          <>
            <Grid
              container
              spacing={3}
              alignItems="stretch"
              style={{ padding: '12px' }}
            >
              {serviceStartDate && isWithinPast60Days(serviceStartDate) && (
                <Grid item xs={12}>
                  <EnergyJourneyCard />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                {eTLoading || pgeETWidgetLoading ? (
                  <Paper>
                    <CardContent
                      css={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgress />
                    </CardContent>
                  </Paper>
                ) : Boolean(pgeETWidgetError) ||
                  (isSchedule32CommercialAccount &&
                    pgeETWidgetData?.getEnergyTrackerData?.detailsAvailable) ? (
                  <CurrentUseCard
                    loading={pgeETWidgetLoading}
                    data={pgeETWidgetData}
                    error={Boolean(pgeETWidgetError)}
                    accountParams={accountParams}
                    isResAccount={isResAccount}
                  />
                ) : (
                  <DashboardStaticCard
                    title={t('BILL_COMPARISON_TITLE')}
                    subText={t('BILL_COMPARISON_SUBTEXT')}
                    footerText={t('VIEW_BILL_DETAILS')}
                    image={<BillComparisonImg />}
                    path={`${ROUTES.VIEW_BILL}#energyUse`}
                  />
                )}
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                style={!isMobile ? { display: 'flex' } : undefined}
              >
                <MyEnergyInsights matchParentHeight={!isMobile} />
              </Grid>

              <Grid item xs={12}>
                <BusinessProgramsCard />
              </Grid>

              <Grid item xs={12}>
                {Boolean(account?.billInfo?.billDetails?.amountDue) && (
                  <ManageAlerts />
                )}
              </Grid>

              <Grid item xs={12}>
                <NextBestOffer accountType="commercial" />
              </Grid>
            </Grid>
          </>
        )}

        {/* hiding marketing cards for now */}
        {false && (
          <Grid item xs={12}>
            <EnergyImpactCarousel isMobile={Boolean(isMobile)} />
          </Grid>
        )}

        <Grid item>
          {isMobile ? (
            <>
              <Grid item xs={12}>
                {Boolean(account?.billInfo?.billDetails?.amountDue) &&
                  isResAccount && <ManageAlerts />}
              </Grid>
              <Hidden smDown>
                <Grid item xs={12} style={{ marginTop: '2em' }}>
                  <Drawer
                    title={t('BILLING_AND_PAYMENT_SETTINGS')}
                    style={{ marginTop: '-2em' }}
                  >
                    {account?.paymentEligibility ||
                    account?.isPaperlessBillEnrolled ||
                    account?.preferredDueDate?.dueDate ||
                    account?.equalpay ? (
                      <ToggleableOptionsListContent
                        isMobile={isMobile}
                        options={getBillingAndPaymentOptions(
                          account,
                          t,
                          iqbdStatus,
                        )}
                      />
                    ) : (
                      <CardContent>
                        {' '}
                        <ErrorOverlay
                          title={t('BILLING_AND_PAYMENT_SETTINGS')}
                          hide
                        />{' '}
                      </CardContent>
                    )}
                  </Drawer>
                </Grid>
              </Hidden>
            </>
          ) : isResAccount ? (
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12}>
                {Boolean(account?.billInfo?.billDetails?.amountDue) && (
                  <ManageAlerts />
                )}
              </Grid>
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
        {isSecureSiteModalEnabled && <AccountDashboardIntercept />}
      </>
    );
  }

  /**
   * This is for resolving a linting error.
   * The code flow expects at least one return statement.
   * Linter isn't smart enough to map the paths covered above.
   */
  return <></>;
}

export default withWidth()(SingleAccountSummary);
